import type { HttpLayer } from '../http.js';
import type { ChatResource } from '../chat/chat-resource.js';
import type { PageWindowCache } from '../cache/list-cache.js';
import type { SearchResponse } from '../types/search.js';
import type {
  DeleteManyResult,
  SessionListBrowsePage,
  SessionListCatalogs,
  SessionListQuery,
  SessionRef,
} from '../types/session-list.js';
import type { SessionsResource } from './sessions.js';
import type { WorkManagementResource } from './work-management.js';
import type { WorkspacesResource } from './workspaces.js';

export const SESSION_LIST_PAGE_SIZE = 50;
export const SESSION_LIST_BULK_DELETE_CAP = 100;

export class SessionListResource {
  constructor(
    private http: HttpLayer,
    private sessions: SessionsResource,
    private workManagement: WorkManagementResource,
    private workspaces: WorkspacesResource,
    private listCache: PageWindowCache<SessionListBrowsePage>,
    private searchCache: PageWindowCache<SearchResponse>,
    private chat: ChatResource,
  ) {}

  /**
   * One recency page of the account-wide session catalog (Postgres), plus the
   * work-item batch for those ids. Catalogs load when `showEmptyGroups` is on,
   * or when grouping needs catalog names for headers (`workspace`, `team`) —
   * a catalog error fails the page (sdk ADR-035).
   *
   * Cache consult: a stored
   * page window serves instantly and revalidates behind the return; the
   * replacement is atomic and the next read sees it.
   */
  async browsePage(
    query: SessionListQuery,
    page: { offset: number },
  ): Promise<SessionListBrowsePage> {
    const identity = await this.http.actingAccountId();
    const key = browsePageKey(query, page.offset);
    const cached = this.listCache.lookup(identity, key);
    if (cached) {
      void this.revalidateBrowse(identity, query, page).catch(() => {
        // no-fallbacks.md exception: the stored window stays readable while
        // the revalidate retry is the next read; a failure here is the
        // quiet-degradation and reconnect heals.
      });
      return cached;
    }
    const fetched = await this.fetchBrowse(query, page);
    this.listCache.store(identity, key, fetched);
    return fetched;
  }

  private async revalidateBrowse(
    identity: string,
    query: SessionListQuery,
    page: { offset: number },
  ): Promise<void> {
    const fresh = await this.fetchBrowse(query, page);
    this.listCache.store(identity, browsePageKey(query, page.offset), fresh);
  }

  private async fetchBrowse(
    query: SessionListQuery,
    page: { offset: number },
  ): Promise<SessionListBrowsePage> {
    const rows = await this.sessions.list(null, {
      limit: SESSION_LIST_PAGE_SIZE + 1,
      offset: page.offset,
    });
    const hasMore = rows.length > SESSION_LIST_PAGE_SIZE;
    const sessions = hasMore ? rows.slice(0, SESSION_LIST_PAGE_SIZE) : rows;
    const workItems =
      sessions.length === 0
        ? []
        : await this.workManagement.getSessionWorkItems(sessions.map((session) => session.id));
    let catalogs: SessionListCatalogs | undefined;
    // Group headers resolve real names from catalogs even with empty groups
    // hidden; a catalog error fails the page (sdk ADR-035).
    if (query.showEmptyGroups || query.grouping === 'workspace' || query.grouping === 'team') {
      const [teams, goals, workspaces] = await Promise.all([
        query.showEmptyGroups || query.grouping === 'team'
          ? this.workManagement.listTeams()
          : Promise.resolve([]),
        query.showEmptyGroups ? this.workManagement.listGoals() : Promise.resolve([]),
        query.showEmptyGroups || query.grouping === 'workspace'
          ? this.workspaces.listNames()
          : Promise.resolve([]),
      ]);
      catalogs = { teams, goals, workspaces };
    }
    return {
      sessions,
      workItems,
      catalogs,
      hasMore,
      nextOffset: page.offset + sessions.length,
    };
  }

  /**
   * One Qdrant page of session-catalog hits. `entity_types` is pinned to
   * `session` — empty-query search is not the conversation list (sdk ADR-035).
   * Cache consult as in {@link browsePage} (search inclusion confirmed).
   */
  async searchPage(params: {
    query: string;
    offset: number;
    workspace_id?: string[];
  }): Promise<SearchResponse> {
    if (params.query === '') {
      throw new Error('sessionList.searchPage: query must be non-empty');
    }
    const identity = await this.http.actingAccountId();
    const key = searchPageKey(params);
    const cached = this.searchCache.lookup(identity, key);
    if (cached) {
      void this.revalidateSearch(identity, params).catch(() => {
        // no-fallbacks.md exception: see browsePage — quiet degradation.
      });
      return cached;
    }
    const fetched = await this.fetchSearch(params);
    this.searchCache.store(identity, key, fetched);
    return fetched;
  }

  private async revalidateSearch(
    identity: string,
    params: { query: string; offset: number; workspace_id?: string[] },
  ): Promise<void> {
    const fresh = await this.fetchSearch(params);
    this.searchCache.store(identity, searchPageKey(params), fresh);
  }

  private async fetchSearch(params: {
    query: string;
    offset: number;
    workspace_id?: string[];
  }): Promise<SearchResponse> {
    return this.http.post('/v1/search', {
      query: params.query,
      entity_types: ['session'],
      sort: 'updated_at_desc',
      limit: SESSION_LIST_PAGE_SIZE + 1,
      offset: params.offset,
      ...(params.workspace_id ? { workspace_id: params.workspace_id } : {}),
    });
  }

  /**
   * Delete the named refs through the account-scoped bulk route, chunked at the
   * server cap. Does not loop `sessions.delete` (sessions ADR-022). The list
   * windows invalidate at the mutation point. A live chat handle on any of the
   * refs is dropped and its event subscription released before the first
   * delete is sent.
   */
  async deleteMany(items: SessionRef[]): Promise<DeleteManyResult> {
    await this.chat._releaseSessions(new Set(items.map((item) => item.session_id)));
    const deleted: SessionRef[] = [];
    const failed: DeleteManyResult['failed'] = [];
    for (let i = 0; i < items.length; i += SESSION_LIST_BULK_DELETE_CAP) {
      const chunk = items.slice(i, i + SESSION_LIST_BULK_DELETE_CAP);
      const result = await this.http.post<DeleteManyResult>('/v1/sessions/sessions/bulk-delete', {
        items: chunk,
      });
      deleted.push(...result.deleted);
      failed.push(...result.failed);
    }
    this.listCache.evictAll();
    this.searchCache.evictAll();
    return { deleted, failed };
  }
}

export function browsePageKey(query: SessionListQuery, offset: number): string {
  return `sessionList.browse|${JSON.stringify(query)}|offset:${offset}`;
}

export function searchPageKey(params: {
  query: string;
  offset: number;
  workspace_id?: string[];
}): string {
  return `sessionList.search|${params.query}|${JSON.stringify(params.workspace_id ?? [])}|offset:${params.offset}`;
}
